import type { RemboursementProformaDto } from '@/features/api/extranet/dto/Demande/PROFORMA'
import type { DispositifDR } from '@/features/domaine/types/opco/Demande/DR'
import { useDemandeProformaStore } from '@/features/interface/demande/proforma/useDemandeProformaStore.ts'
import { proformaService } from '@/features/services/demande'
import { isNotNullOrUndefined } from '@/features/shared/tools'
import { computed, type Ref, ref, shallowRef, watch } from 'vue'

export function useRechercherDR(visible: Ref<boolean>) {
  const drCodesSelectionnés = ref<string[] | null>()
  const strRecherche = ref<string>('')
  const drTous = shallowRef<RemboursementProformaDto[] | null>(null)
  const store = useDemandeProformaStore()

  // Filtres
  const filtresDispositifs = ref<DispositifDR[]>([])
  const toutSelectionner = ref<boolean>(false)

  const modaleVisible = computed({
    get: () => visible.value && isNotNullOrUndefined(drTous.value),
    set: (value: boolean) => (visible.value = value ?? false),
  })

  watch(
    () => visible.value,
    async (newValue, oldValue) => {
      if (newValue === oldValue) return
      if (newValue) {
        try {
          drTous.value = await loadData()
          drCodesSelectionnés.value = []
          resetFiltres()
        } catch {
          modaleVisible.value = false
        }
      } else {
        drTous.value = null
        drCodesSelectionnés.value = null
        strRecherche.value = ''
        resetFiltres()
      }
    },
  )

  // Chargement DR
  async function loadData() {
    // Mode PRODUCTION - API réelle
    const id = store.model!.id
    return await proformaService.loadDrEligibles(id)
  }

  // Réinitialiser les filtres
  function resetFiltres() {
    filtresDispositifs.value = []
    strRecherche.value = ''
    toutSelectionner.value = false
  }

  /* Liste les DR éligibles (non déjà ajoutées + filtres machine à état) */
  const drEligibles = computed(() => {
    if (!drTous.value) return null

    const siretProforma = store.model?.etablissementAdherent?.siret?.siren
    const typePublicProforma = store.model?.typePublic

    return drTous.value.filter((item) => {
      // Exclure les DR déjà ajoutées
      if (store.model?.remboursements.some((remb) => remb.code === item.code)) {
        return false
      }

      // Si typePublic = Permanent, exclure les dispositifs FPE
      if (typePublicProforma === 'Permanent' && item.dispositif === 'FPE') {
        return false
      }

      // Ne garder que les DR de l'établissement sélectionné (même SIRET)
      // Note: En mode mock, on ignore ce filtre car les données ne correspondent pas
      if (siretProforma && item.etablissementEmetteur?.siret?.siren !== siretProforma) {
        return false
      }

      return true
    })
  })

  /* Liste les DR après application des filtres (AVANT pagination) */
  const drFiltrées = computed(() => {
    if (!drEligibles.value) return null

    let filtered = drEligibles.value

    // Filtre par Dispositifs (multiple)
    if (filtresDispositifs.value && filtresDispositifs.value.length > 0) {
      filtered = filtered.filter((item) => filtresDispositifs.value.includes(item.dispositif))
    }

    // Filtre par recherche textuelle
    const token = strRecherche.value?.trim()?.toLowerCase()
    if (token) {
      filtered = filtered.filter((item) => {
        const fullText = [
          item.code,
          item.dispositif,
          item.codeDossier,
          item.reference,
          item.etablissementEmetteur?.denomination,
          item.montantHt?.toString(),
        ]
          .filter(Boolean)
          .join('_')
          .toLowerCase()
        return fullText.includes(token)
      })
    }

    return filtered.sort((itemA, itemB) => (itemA.code < itemB.code ? -1 : 1))
  })

  /* Calcul du nombre total de DR (filtrées, pas seulement la page) */
  const nombreTotalDR = computed(() => drFiltrées.value?.length ?? 0)

  /* Calcul du montant total de toutes les DR filtrées */
  const montantTotalDR = computed(() => {
    if (!drFiltrées.value) return 0
    return drFiltrées.value.reduce((sum, dr) => sum + (dr.montantHt ?? 0), 0)
  })

  /* Calcul du nombre de DR sélectionnées */
  const nombreDRSelectionnees = computed(() => drCodesSelectionnés.value?.length ?? 0)

  /* Calcul du montant des DR sélectionnées */
  const montantDRSelectionnees = computed(() => {
    if (!drFiltrées.value || !drCodesSelectionnés.value) return 0
    return drFiltrées.value
      .filter((dr) => drCodesSelectionnés.value!.includes(dr.code))
      .reduce((sum, dr) => sum + (dr.montantHt ?? 0), 0)
  })

  /* Toggle de la sélection globale */
  watch(
    () => toutSelectionner.value,
    (newValue) => {
      if (!drFiltrées.value || !drCodesSelectionnés.value) return

      if (newValue) {
        // Sélectionner toutes les DR filtrées
        const codesFiltrés = drFiltrées.value.map((dr) => dr.code)
        drCodesSelectionnés.value = [...new Set([...drCodesSelectionnés.value, ...codesFiltrés])]
      } else {
        // Désélectionner toutes les DR filtrées
        const codesFiltrés = drFiltrées.value.map((dr) => dr.code)
        drCodesSelectionnés.value = drCodesSelectionnés.value.filter((code) => !codesFiltrés.includes(code))
      }
    },
  )

  /* Vérifier si une DR est sélectionnée */
  const isDRSelectionnee = (code: string) => {
    return drCodesSelectionnés.value?.includes(code) ?? false
  }

  /* Toggle sélection d'une DR */
  const toggleDRSelection = (code: string) => {
    if (drCodesSelectionnés.value) {
      if (!drCodesSelectionnés.value.includes(code)) {
        drCodesSelectionnés.value.push(code)
      } else {
        drCodesSelectionnés.value = drCodesSelectionnés.value.filter((item) => item !== code)
      }
    }
  }

  const drAjouter = async () => {
    // Ajouter les DR à la demande
    const drAjoutées = drTous.value!.filter((dr) => drCodesSelectionnés.value!.includes(dr.code))
    const updateDR = store.setupCustomUpdater<void>((patch) => {
      patch.remboursements!.push(...drAjoutées)
    })
    await updateDR()

    // Fermer la popup
    modaleVisible.value = false
  }

  const drEligiblesNb = computed(() => drEligibles.value?.length)

  /* Liste des dispositifs disponibles pour le filtre dropdown */
  const dispositifItems = computed(() => {
    const allItems = [
      { id: 'CPRO', label: 'Contrat de professionnalisation' },
      { id: 'PROA', label: 'Reconversion ou promotion par alternance' },
      { id: 'AF', label: 'Action de formation' },
      { id: 'FPE', label: 'FPE' },
    ]

    // Si typePublic = Permanent, exclure FPE
    if (store.model?.typePublic === 'Permanent') {
      return allItems.filter((item) => item.id !== 'FPE')
    }

    return allItems
  })

  return {
    modaleVisible,
    strRecherche,
    drFiltrées,
    drAjouter,
    drEligiblesNb,
    drCodesSelectionnés,
    // Filtres
    filtresDispositifs,
    dispositifItems,
    resetFiltres,
    // Sélection
    toutSelectionner,
    isDRSelectionnee,
    toggleDRSelection,
    // Totaux
    nombreTotalDR,
    montantTotalDR,
    nombreDRSelectionnees,
    montantDRSelectionnees,
  }
}
